import { supabase } from '../lib/supabase';

/**
 * StudentVerificationService
 *
 * Handles student manual verification and editing of college application data:
 * - Deadlines (override AI-generated dates from global_colleges)
 * - Essay prompts (add/edit/delete with proper timeline_items propagation)
 * - Application requirements (override global_colleges components)
 *
 * All edits are marked as "manually_verified" to prioritize student input over AI.
 */

export class StudentVerificationService {
  /**
   * Get effective deadline for a college (manual override takes precedence)
   * @param {Object} college - College object from college_journey_colleges (with global_college joined)
   * @param {string} deadlineType - e.g., 'ed_deadline', 'ea_deadline', 'rd_deadline'
   * @returns {string|null} - ISO date string or null
   */
  static getEffectiveDeadline(college, deadlineType) {
    const manualField = `${deadlineType}_manual`;
    return college[manualField] || college.global_college?.[deadlineType] || null;
  }

  /**
   * Update a deadline with manual override
   * Propagates changes to related timeline_items (deadline reminders)
   *
   * @param {string} collegeId - college_journey_colleges.id
   * @param {string} deadlineType - 'ed_deadline', 'ea_deadline', 'rd_deadline', etc.
   * @param {string} newDate - ISO date string (YYYY-MM-DD)
   * @param {string} userId - User making the change
   * @returns {Promise<{success: boolean, updatedTasksCount: number, error?: string}>}
   */
  static async updateDeadline(collegeId, deadlineType, newDate, userId) {
    try {
      const manualField = `${deadlineType}_manual`;

      // 1. Update college_journey_colleges with manual override
      const { error: collegeError } = await supabase
        .from('college_journey_colleges')
        .update({
          [manualField]: newDate,
          deadlines_manually_verified: true,
          deadlines_verified_at: new Date().toISOString(),
          deadlines_verified_by: userId
        })
        .eq('id', collegeId);

      if (collegeError) {
        console.error('Error updating college deadline:', collegeError);
        return { success: false, updatedTasksCount: 0, error: collegeError.message };
      }

      // 2. Find related timeline_items to update
      // These are deadline reminder tasks (created by generate_deadline_reminder_tasks trigger)
      const { data: timelineItems, error: fetchError } = await supabase
        .from('timeline_items')
        .select('id, metadata')
        .eq('college_id', collegeId)
        .or(`source_type.eq.deadline_reminder,metadata->>deadline_type.eq.${deadlineType.replace('_deadline', '')}`);

      if (fetchError) {
        console.error('Error fetching timeline items:', fetchError);
        return { success: true, updatedTasksCount: 0, warning: 'College updated but could not update timeline items' };
      }

      if (!timelineItems || timelineItems.length === 0) {
        return { success: true, updatedTasksCount: 0, message: 'No related timeline items to update' };
      }

      // 3. Update timeline_items with new deadline
      // Set due_date = hard_deadline = new deadline
      // Set scheduled_date = deadline - 3 days (buffer for preparation)
      const scheduledDate = new Date(newDate);
      scheduledDate.setDate(scheduledDate.getDate() - 3);

      const updatePromises = timelineItems.map(item => {
        const updatedMetadata = {
          ...(item.metadata || {}),
          manually_verified: true,
          verified_by: userId,
          verified_at: new Date().toISOString(),
          original_deadline: item.metadata?.original_deadline || newDate
        };

        return supabase
          .from('timeline_items')
          .update({
            due_date: newDate,
            hard_deadline: newDate,
            scheduled_date: scheduledDate.toISOString().split('T')[0],
            metadata: updatedMetadata
          })
          .eq('id', item.id);
      });

      const results = await Promise.all(updatePromises);
      const errors = results.filter(r => r.error);

      if (errors.length > 0) {
        console.error('Some timeline items failed to update:', errors);
        return {
          success: true,
          updatedTasksCount: timelineItems.length - errors.length,
          warning: `${errors.length} timeline items failed to update`
        };
      }

      return {
        success: true,
        updatedTasksCount: timelineItems.length,
        message: `Updated ${timelineItems.length} related timeline tasks`
      };

    } catch (error) {
      console.error('Error in updateDeadline:', error);
      return { success: false, updatedTasksCount: 0, error: error.message };
    }
  }

  /**
   * Update essay prompts for a college
   * Handles add/edit/delete with proper timeline_items propagation
   *
   * CRITICAL: Each essay_slot has 6 related timeline_items created by generate_essay_workflow_tasks trigger
   * - source_id pattern: 'essay_slot_{essay_slot_id}_task_{task_sequence}'
   * - task_sequence: 1-6 (brainstorm, draft, review, feedback, polish, submit)
   *
   * @param {string} collegeId - college_journey_colleges.id
   * @param {Array} newPrompts - Array of {prompt_text, word_limit, slot_type, essay_slot}
   * @param {string} userId - User making the change
   * @returns {Promise<{success: boolean, added: number, updated: number, deleted: number, error?: string}>}
   */
  static async updateEssayPrompts(collegeId, newPrompts, userId) {
    try {
      // 1. Get current essay_slots for this college
      const { data: currentSlots, error: fetchError } = await supabase
        .from('essay_slots')
        .select('*')
        .eq('college_id', collegeId)
        .neq('slot_type', 'personal_statement'); // Don't touch personal statement

      if (fetchError) {
        console.error('Error fetching current essay slots:', fetchError);
        return { success: false, added: 0, updated: 0, deleted: 0, error: fetchError.message };
      }

      const currentSlotsMap = new Map(currentSlots.map(slot => [
        `${slot.slot_type}_${slot.essay_slot}`,
        slot
      ]));

      const newPromptsMap = new Map(newPrompts.map(prompt => [
        `${prompt.slot_type}_${prompt.essay_slot}`,
        prompt
      ]));

      let addedCount = 0;
      let updatedCount = 0;
      let deletedCount = 0;

      // 2. Determine changes
      const toAdd = [];
      const toUpdate = [];
      const toDelete = [];

      // Find new and modified essays
      for (const [key, newPrompt] of newPromptsMap.entries()) {
        const existing = currentSlotsMap.get(key);
        if (!existing) {
          toAdd.push(newPrompt);
        } else if (
          existing.prompt_text !== newPrompt.prompt_text ||
          existing.word_limit !== newPrompt.word_limit ||
          existing.friendly_title !== newPrompt.friendly_title ||
          existing.is_optional !== newPrompt.is_optional
        ) {
          toUpdate.push({ ...newPrompt, id: existing.id });
        }
      }

      // Find deleted essays
      for (const [key, existing] of currentSlotsMap.entries()) {
        if (!newPromptsMap.has(key)) {
          toDelete.push(existing);
        }
      }

      // 3. Process ADDED essays
      for (const newEssay of toAdd) {
        const { error: insertError } = await supabase
          .from('essay_slots')
          .insert({
            user_id: userId,
            college_id: collegeId,
            slot_type: newEssay.slot_type,
            essay_slot: newEssay.essay_slot,
            prompt_text: newEssay.prompt_text,
            word_limit: newEssay.word_limit,
            friendly_title: newEssay.friendly_title || null,
            is_optional: newEssay.is_optional || false,
            status: 'empty',
            manually_verified: true,
            manually_verified_at: new Date().toISOString(),
            manually_verified_by: userId
          });

        if (insertError) {
          console.error('Error adding essay slot:', insertError);
        } else {
          addedCount++;
          // Note: generate_essay_workflow_tasks trigger auto-creates 6 timeline_items
        }
      }

      // 4. Process MODIFIED essays
      for (const modifiedEssay of toUpdate) {
        // Get existing slot to check if it's part of a choice group
        const { data: existingSlot } = await supabase
          .from('essay_slots')
          .select('prompt_group_id, prompt_choice_type, selection_status')
          .eq('id', modifiedEssay.id)
          .single();

        // Prepare update data - preserve choice group metadata if it exists
        const updateData = {
          prompt_text: modifiedEssay.prompt_text,
          word_limit: modifiedEssay.word_limit,
          friendly_title: modifiedEssay.friendly_title || null,
          is_optional: modifiedEssay.is_optional || false,
          manually_verified: true,
          manually_verified_at: new Date().toISOString(),
          manually_verified_by: userId
        };

        // DON'T overwrite choice group fields when manually editing
        // These fields should only be changed through choice group-specific methods
        // (Do not include: prompt_group_id, prompt_choice_type, selection_status, group_label, prompt_option_id)

        // Update essay_slot
        const { error: updateError } = await supabase
          .from('essay_slots')
          .update(updateData)
          .eq('id', modifiedEssay.id);

        if (updateError) {
          console.error('Error updating essay slot:', updateError);
          continue;
        }

        // Update related 6 timeline_items
        const { data: relatedTasks, error: taskFetchError } = await supabase
          .from('timeline_items')
          .select('id, title, metadata')
          .like('source_id', `essay_slot_${modifiedEssay.id}_task_%`);

        if (taskFetchError) {
          console.error('Error fetching related tasks:', taskFetchError);
        } else if (relatedTasks && relatedTasks.length > 0) {
          // Use friendly_title if available, otherwise use prompt preview
          const essayIdentifier = modifiedEssay.friendly_title || modifiedEssay.prompt_text.substring(0, 50);
          const essaySuffix = modifiedEssay.friendly_title ? '' : '...';

          const taskUpdates = relatedTasks.map(task => {
            const updatedMetadata = {
              ...(task.metadata || {}),
              manually_verified: true,
              verified_by: userId,
              verified_at: new Date().toISOString(),
              essay_prompt: modifiedEssay.prompt_text,
              word_limit: modifiedEssay.word_limit,
              friendly_title: modifiedEssay.friendly_title || null
            };

            // Update title to reflect friendly_title or prompt (preserve task type prefix)
            const taskTypeMatch = task.title.match(/^(Brainstorm Concepts|Write First Draft|Generate First Draft Analysis|Get Essay Feedback|Revise and Polish Essay|Finalize Essay)/);
            const taskType = taskTypeMatch ? taskTypeMatch[1] : '';
            const newTitle = taskType ? `${taskType}: ${essayIdentifier}${essaySuffix}` : task.title;

            return supabase
              .from('timeline_items')
              .update({
                title: newTitle,
                metadata: updatedMetadata
              })
              .eq('id', task.id);
          });

          await Promise.all(taskUpdates);
        }

        updatedCount++;
      }

      // 5. Process DELETED essays
      for (const deletedEssay of toDelete) {
        // Check if essay has existing work
        if (deletedEssay.assigned_essay_id || deletedEssay.status !== 'empty') {
          console.warn(`Essay slot ${deletedEssay.id} has existing work but is being deleted`);
          // TODO: Add confirmation UI before allowing delete with existing work
        }

        const { error: deleteError } = await supabase
          .from('essay_slots')
          .delete()
          .eq('id', deletedEssay.id);

        if (deleteError) {
          console.error('Error deleting essay slot:', deleteError);
        } else {
          deletedCount++;
          // Note: CASCADE delete will remove 6 related timeline_items automatically
        }
      }

      // 6. Update college verification status
      if (addedCount > 0 || updatedCount > 0 || deletedCount > 0) {
        await supabase
          .from('college_journey_colleges')
          .update({
            essay_verification_status: 'manually_verified'
          })
          .eq('id', collegeId);
      }

      return {
        success: true,
        added: addedCount,
        updated: updatedCount,
        deleted: deletedCount,
        message: `Essays updated: ${addedCount} added, ${updatedCount} modified, ${deletedCount} deleted`
      };

    } catch (error) {
      console.error('Error in updateEssayPrompts:', error);
      return { success: false, added: 0, updated: 0, deleted: 0, error: error.message };
    }
  }

  /**
   * Update application requirements override
   *
   * @param {string} collegeId - college_journey_colleges.id
   * @param {Object} requirements - {required: [...], optional: [...]}
   * @param {string} userId - User making the change
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  static async updateApplicationRequirements(collegeId, requirements, userId) {
    try {
      const { error } = await supabase
        .from('college_journey_colleges')
        .update({
          application_requirements_override: requirements,
          requirements_manually_verified: true,
          requirements_verified_at: new Date().toISOString(),
          requirements_verified_by: userId
        })
        .eq('id', collegeId);

      if (error) {
        console.error('Error updating application requirements:', error);
        return { success: false, error: error.message };
      }

      // TODO: Update related timeline_items with source_type='milestone_template'
      // and category='application' to reflect new requirements in descriptions

      return {
        success: true,
        message: 'Application requirements updated successfully'
      };

    } catch (error) {
      console.error('Error in updateApplicationRequirements:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get verification status for a college
   * Returns which data has been manually verified
   *
   * @param {string} collegeId - college_journey_colleges.id
   * @returns {Promise<{deadlines: boolean, essays: boolean, requirements: boolean}>}
   */
  static async getVerificationStatus(collegeId) {
    try {
      const { data, error } = await supabase
        .from('college_journey_colleges')
        .select('deadlines_manually_verified, essay_verification_status, requirements_manually_verified')
        .eq('id', collegeId)
        .single();

      if (error) {
        console.error('Error fetching verification status:', error);
        return { deadlines: false, essays: false, requirements: false };
      }

      return {
        deadlines: data?.deadlines_manually_verified || false,
        essays: data?.essay_verification_status === 'manually_verified',
        requirements: data?.requirements_manually_verified || false
      };

    } catch (error) {
      console.error('Error in getVerificationStatus:', error);
      return { deadlines: false, essays: false, requirements: false };
    }
  }
}

export default StudentVerificationService;
